import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement))
      throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement))
      throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, {
      signal,
    });

    /*
     * Collapse other accordions within the same group
     * Currently scoped to footer menu group
     */
    this.details.addEventListener(
      'toggle',
      () => {
        const parentGroup = this.closest('.ea-footer-menu-group');
        if (!parentGroup) return;

        const isMobile = isMobileBreakpoint();
        const isDesktop = !isMobile;

        // Respect disable flags
        if (
          (isMobile && this.#disableOnMobile) ||
          (isDesktop && this.#disableOnDesktop)
        ) {
          return;
        }

        if (!this.details.open) return;

        const accordions = parentGroup.querySelectorAll('accordion-custom');

        accordions.forEach((accordion) => {
          if (accordion === this) return;

          const details = accordion.querySelector('details');
          if (details) details.open = false;
        });
      },
      { signal },
    );
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if (
      (isMobile && this.#disableOnMobile) ||
      (isDesktop && this.#disableOnDesktop)
    ) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

/*
 * Accordion dividers — put the bottom divider on the LAST VISIBLE row.
 *
 * A row is hidden (display:none) when its content is empty/[hidden] (see blocks/_accordion-row.liquid).
 * CSS :last-child still counts a hidden row, and CSS can't select the "last visible" child — so a
 * pure-CSS bottom divider can land on the invisible row and the real last row loses its line. This
 * marks the last row that is actually visible so blocks/accordion.liquid can draw the line on it
 * (hugging the content, padding-safe). Fail-safe: without this, the CSS container fallback still shows
 * a divider. Scoped to `.accordion--dividers`, so it's a no-op on pages without one and never touches
 * filter/footer accordions.
 */
(() => {
  const CONTAINER = '.accordion--dividers';
  const ROW = ':scope > accordion-custom';
  const LAST_VISIBLE = 'accordion-row--last-visible';
  const observed = new WeakSet();
  const scheduled = new WeakMap();

  const mark = (container) => {
    const rows = container.querySelectorAll(ROW);
    let last = null;
    // Computed display mirrors the CSS hide rule exactly, so this stays correct on live changes too.
    for (const row of rows) if (getComputedStyle(row).display !== 'none') last = row;
    rows.forEach((row) => row.classList.toggle(LAST_VISIBLE, row === last));
  };

  const schedule = (container) => {
    if (scheduled.get(container)) return;
    scheduled.set(container, true);
    requestAnimationFrame(() => {
      scheduled.set(container, false);
      mark(container);
    });
  };

  const setup = (container) => {
    if (observed.has(container)) return;
    observed.add(container);
    // Mark first, then hand the divider over — so there's no frame where the fallback is off and no
    // row is marked yet (which would flash a missing bottom line).
    mark(container);
    container.setAttribute('data-js-dividers', '');
    // Re-check when rows are added/removed or a descendant's [hidden] flips (e.g. a variant change).
    new MutationObserver(() => schedule(container)).observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
  };

  const init = () => document.querySelectorAll(CONTAINER).forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init); // theme editor re-renders a section
})();
